import React from 'react';

import { Alert, Box, Chip, Link } from '@mui/material';
import { DataGrid, GridColDef, GridToolbar } from '@mui/x-data-grid';

import { useGetMmaoOffers } from '~/hooks/apis/accommodation/useMmaoOffers';

import { MmaoOffer } from '~/services/AccommodationService';

import { MmaoOffersSearchInput } from './utils';

interface Props {
  searchInput: MmaoOffersSearchInput;
  pageIndex?: number;
  onPageChange: (pageIndex: number) => void;
}

const PAGE_SIZE = 20;

function MmaoOffersResultsTable({ searchInput, pageIndex, onPageChange }: Props) {
  const { data, isLoading, error } = useGetMmaoOffers({
    offerId: searchInput.offerId,
    createdBy: searchInput.createdBy,
    updatedAtFrom: searchInput.updatedAtFrom,
    updatedAtTo: searchInput.updatedAtTo,
    page: String((pageIndex ?? 0) + 1),
    limit: String(PAGE_SIZE),
  });

  const columns: Array<GridColDef<MmaoOffer>> = [
    {
      field: 'offerId',
      headerName: 'Offer ID',
      minWidth: 200,
      flex: 2,
      display: 'flex',
      hideable: false,
      renderCell: (params) => (
        <Link href={`/offers/${params.value}`} target="_blank">
          {params.value}
        </Link>
      ),
    },
    {
      field: 'offerName',
      headerName: 'Offer Name',
      minWidth: 200,
      flex: 2.5,
      display: 'flex',
    },
    {
      field: 'propertyId',
      headerName: 'Property ID',
      minWidth: 150,
      flex: 1.5,
      display: 'flex',
    },
    {
      field: 'vendorId',
      headerName: 'Vendor ID',
      minWidth: 120,
      flex: 1,
      display: 'flex',
    },
    {
      field: 'offerType',
      headerName: 'Offer Type',
      width: 140,
      display: 'flex',
      renderCell: (params) => <Chip label={params.value} size="small" variant="outlined" />,
    },
    {
      field: 'mode',
      headerName: 'Mode',
      width: 140,
      display: 'flex',
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          color={params.value === 'prod-import' ? 'primary' : 'default'}
          variant="outlined"
        />
      ),
    },
    {
      field: 'createdBy',
      headerName: 'Created By',
      minWidth: 150,
      flex: 1.5,
      display: 'flex',
    },
    {
      field: 'selfDestructDate',
      headerName: 'Self Destruct Date',
      width: 160,
      display: 'flex',
      valueGetter: (_value, row) => (row.selfDestructDate ? new Date(row.selfDestructDate).toLocaleDateString() : '-'),
    },
    {
      field: 'updatedAt',
      headerName: 'Updated At',
      width: 160,
      display: 'flex',
      valueGetter: (_value, row) => new Date(row.updatedAt).toLocaleString(),
    },
    {
      field: 'createdAt',
      headerName: 'Created At',
      width: 160,
      display: 'flex',
      valueGetter: (_value, row) => new Date(row.createdAt).toLocaleString(),
    },
  ];

  return (
    <>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error.message}
        </Alert>
      )}
      <Box
        sx={{
          width: '100%',
          overflowX: 'auto',
          '& .MuiDataGrid-root': {
            minWidth: { xs: 600, sm: 800, md: '100%' },
          },
        }}
      >
        <DataGrid
          columns={columns}
          rows={data?.mmaoOffers ?? []}
          getRowId={(row) => row.id}
          rowCount={data?.total}
          getRowHeight={() => 60}
          pageSizeOptions={[PAGE_SIZE]}
          paginationMode="server"
          paginationModel={{ page: pageIndex ?? 0, pageSize: PAGE_SIZE }}
          onPaginationModelChange={({ page }) => onPageChange(page)}
          loading={isLoading}
          autoHeight
          disableColumnFilter
          disableDensitySelector
          initialState={{
            columns: {
              columnVisibilityModel: {
                vendorId: false,
                createdAt: false,
              },
            },
          }}
          slots={{ toolbar: GridToolbar }}
          slotProps={{ toolbar: { showQuickFilter: true } }}
        />
      </Box>
    </>
  );
}

export default MmaoOffersResultsTable;
